import type DashboardMetricsRepository from "@/core/dashboard/repositories/DashboardMetricsRepository";
import type { ApiP1MetricsResponse, P1Metrics } from "@/core/dashboard/entities/P1Metrics";
import type { ApiP2MetricsResponse, P2Metrics } from "@/core/dashboard/entities/P2Metrics";
import type { ApiP3MetricsResponse, P3Metrics } from "@/core/dashboard/entities/P3Metrics";
import type { ApiP4MetricsResponse, P4Metrics } from "@/core/dashboard/entities/P4Metrics";
import type {
  CommercialCustomersFilters,
  CommercialCustomersResult,
} from "@/core/dashboard/entities/CommercialCustomer";
import type { MetricsDateRange } from "@/core/dashboard/entities/MetricsDateRange";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "@/details/data/backend/rest/http/HttpClient";
import { ApiResponse } from "@/details/data/backend/rest/api/types/ApiResponse";
import { executeRequest } from "@/details/data/backend/rest/http/services/executeRequest/executeRequest";
import handleRequestError from "@/details/data/backend/rest/http/services/handleRequestError/handleRequestError";
import {
  getCommercialCustomersUrl,
  getP1MetricsUrl,
  getP2MetricsUrl,
  getP3MetricsUrl,
  getP4MetricsUrl,
} from "@/details/data/backend/rest/api/dashboardBackendEndpoints/dashboardBackendEndpoints";
import P1MetricsMapper from "@/core/dashboard/mappers/P1MetricsMapper";
import P2MetricsMapper from "@/core/dashboard/mappers/P2MetricsMapper";
import P3MetricsMapper from "@/core/dashboard/mappers/P3MetricsMapper";
import P4MetricsMapper from "@/core/dashboard/mappers/P4MetricsMapper";
import CommercialCustomersMapper, {
  ApiCommercialCustomersResponse,
} from "@/core/dashboard/mappers/CommercialCustomersMapper";

/**
 * Real HTTP implementation for DashboardMetricsRepository.
 *
 * Currently inactive — swap in ServiceContainer.ts when the API is ready:
 *   Replace: new FakeDashboardMetricsRepositoryBackend()
 *   With:    new DashboardMetricsRepositoryBackend(httpClient)
 */
export default class DashboardMetricsRepositoryBackend
  implements DashboardMetricsRepository
{
  constructor(private readonly httpClient: HttpClient) {}

  /** Shared ApiResponse<T> wrapper handling for the /metrics endpoints. */
  private fetchMetrics<TApi, TDomain>(
    url: string,
    toDomain: (api: TApi) => TDomain,
  ): Promise<RepositoryResult<TDomain>> {
    return executeRequest(
      () => this.httpClient.get<ApiResponse<TApi>>({ url, withAuth: true }),
      toDomain,
    );
  }

  getP1Metrics(range?: MetricsDateRange): Promise<RepositoryResult<P1Metrics>> {
    return this.fetchMetrics<ApiP1MetricsResponse, P1Metrics>(
      getP1MetricsUrl(range),
      (api) => P1MetricsMapper.toDomain(api),
    );
  }

  getP2Metrics(range?: MetricsDateRange): Promise<RepositoryResult<P2Metrics>> {
    return this.fetchMetrics<ApiP2MetricsResponse, P2Metrics>(
      getP2MetricsUrl(range),
      (api) => P2MetricsMapper.toDomain(api),
    );
  }

  getP3Metrics(range?: MetricsDateRange): Promise<RepositoryResult<P3Metrics>> {
    return this.fetchMetrics<ApiP3MetricsResponse, P3Metrics>(
      getP3MetricsUrl(range),
      (api) => P3MetricsMapper.toDomain(api),
    );
  }

  getP4Metrics(range?: MetricsDateRange): Promise<RepositoryResult<P4Metrics>> {
    return this.fetchMetrics<ApiP4MetricsResponse, P4Metrics>(
      getP4MetricsUrl(range),
      (api) => P4MetricsMapper.toDomain(api),
    );
  }

  async getCommercialCustomers(
    filters: CommercialCustomersFilters,
  ): Promise<RepositoryResult<CommercialCustomersResult>> {
    try {
      // PaginatedResponse<T> — no ApiResponse wrapper.
      const response =
        await this.httpClient.get<ApiCommercialCustomersResponse>({
          url: getCommercialCustomersUrl(filters),
          withAuth: true,
        });

      return RepositoryResultFactory.success(
        CommercialCustomersMapper.toDomain(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }
}
